import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Image, FolderOpen } from 'lucide-react';
import MediaLibrary from './MediaLibrary';

const ImagePickerDialog = ({ 
  onSelect, 
  currentImage = '', 
  buttonText = 'Välj från bibliotek', 
  buttonVariant = 'outline',
  // Support controlled mode
  open: controlledOpen,
  onClose 
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use controlled or uncontrolled mode
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  
  const handleOpenChange = (newOpen) => {
    if (isControlled) {
      if (!newOpen && onClose) {
        onClose();
      }
    } else {
      setInternalOpen(newOpen);
    }
  };

  const handleSelect = (imageUrl) => {
    onSelect(imageUrl);
    handleOpenChange(false);
  };

  // If in controlled mode, don't render the trigger button
  if (isControlled) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" aria-describedby="image-picker-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-600" />
              Välj bild från biblioteket
            </DialogTitle>
          </DialogHeader>
          <p id="image-picker-description" className="sr-only">Välj en bild från bildbiblioteket</p>
          
          {/* Current image preview */}
          {currentImage && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500 mb-2">Nuvarande bild:</p>
              <div className="flex items-center gap-3">
                <img 
                  src={currentImage} 
                  alt="Current" 
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <p className="text-sm text-gray-600 truncate flex-1">{currentImage}</p>
              </div>
            </div>
          )}
          
          <MediaLibrary isSelector={true} onSelect={handleSelect} />
        </DialogContent>
      </Dialog>
    );
  }

  // Uncontrolled mode with trigger button
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant={buttonVariant} className="gap-2">
          <FolderOpen className="w-4 h-4" />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" aria-describedby="image-picker-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-blue-600" />
            Välj bild från biblioteket
          </DialogTitle>
        </DialogHeader>
        <p id="image-picker-description" className="sr-only">Välj en bild från bildbiblioteket</p>
        
        {/* Current image preview */}
        {currentImage && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
            <p className="text-xs text-gray-500 mb-2">Nuvarande bild:</p>
            <div className="flex items-center gap-3">
              <img 
                src={currentImage} 
                alt="Current" 
                className="w-16 h-16 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 truncate flex-1">{currentImage}</p>
            </div>
          </div>
        )}
        
        <MediaLibrary isSelector={true} onSelect={handleSelect} />
      </DialogContent>
    </Dialog>
  );
};

export default ImagePickerDialog;
